import React, { useEffect, useState, useRef } from "react";
import {
  Spin,
  Table,
  Button,
  Input,
  Space,
  Form,
  InputNumber,
  notification,
  Modal,
  Row,
  Col,
  Select,
  Card,
  Alert,
  Tooltip,
  Tag,
  Collapse,
} from "antd";
import {
  LoadingOutlined,
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Highlighter from "react-highlight-words";
import ModalIndicadores from "../../components/evaluacionDesempeno/modalIndicadores";
import ModalPuestos from "../../components/evaluacionDesempeno/modalPuestos";
import {
  getIndicadores,
  getIndicadoresPorPuesto,
  updateResultado,
} from "../../actions/evaluacionDesempeño";
import "./evaluacionDesempeno.css";

const { confirm } = Modal;

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  onSave,
  onCancel,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <div>
          <Form.Item
            name={dataIndex}
            style={{
              margin: 0,
            }}
            rules={[
              {
                required: true,
                message: `Por favor ingrese ${title}`,
              },
              {
                type: "number",
                min: 0,
                max: 100,
                transform: (value) => Number(value),
                message: "El valor debe ser un número entre 0 y 100",
              },
            ]}
          >
            {inputNode}
          </Form.Item>
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => onSave(record.key, dataIndex)}
              icon={<CheckOutlined />}
              style={{ marginRight: 8 }}
            />
            <Button onClick={onCancel} icon={<CloseOutlined />} />
          </div>
        </div>
      ) : (
        children
      )}
    </td>
  );
};

function EvaluacionDesempeno(props) {
  const dispatch = useDispatch();
  const { indicadores, loading, error } = useSelector(
    (state) => state.evaluacionDesempeño
  );
  const user = JSON.parse(localStorage.getItem("@@auth_user"));
  const [isIndicadoresModalVisible, setIsIndicadoresModalVisible] =
    useState(false);
  const [isPuestosModalVisible, setIsPuestosModalVisible] = useState(false);
  const [isResultadoModalVisible, setIsResultadoModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editingColumn, setEditingColumn] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [globalSearch, setGlobalSearch] = useState("");

  const currentYear = new Date().getFullYear();
  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const currentMonth = new Date().getMonth();
  const registrationDeadline = new Date(currentYear, currentMonth, 120);

  useEffect(() => {
    // Usuarios administradores:
    // Gerente de capital humano y desarrollo organizacional: 4
    // Director de Operaciones: 20
    // Coordinador de desarrollo organizacional: 19
    if ([4, 20, 19].includes(user.puesto)) {
      dispatch(getIndicadores(selectedYear));
    } else {
      dispatch(getIndicadoresPorPuesto(selectedYear));
    }
  }, [dispatch, user.puesto, selectedYear]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      closeDropdown,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Resetear
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filtrar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              closeDropdown();
            }}
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const isEditing = (record, column) =>
    record.key === editingKey && column === editingColumn;

  const edit = (record, column) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.key);
    setEditingColumn(column);
  };

  const cancel = () => {
    setEditingKey("");
    setEditingColumn("");
  };

  const save = async (key, column) => {
    try {
      const row = await form.validateFields();
      const month = parseInt(column.replace("mes", ""), 10);
      const data = {
        indicadorId: key,
        mes: month,
        year: selectedYear,
        resultado: parseFloat(row[column]),
      };

      dispatch(updateResultado(data));
      setEditingKey("");
      setEditingColumn("");
    } catch (errInfo) {
      console.log("Error al guardar:", errInfo);
    }
  };

  const isMonthEditable = (monthIndex) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-based: enero=0, junio=5, etc.
    const currentYear = currentDate.getFullYear();

    // Si estamos viendo el año actual
    if (selectedYear === currentYear) {
      // Permitir edición para el mes actual y los 2 meses anteriores
      return monthIndex === currentMonth || 
             monthIndex === currentMonth - 1 || 
             monthIndex === currentMonth - 2;
    }
    
    // Si estamos viendo el año anterior
    if (selectedYear === currentYear - 1) {
      // Solo permitir editar diciembre (mes 11) si estamos en enero o febrero del año actual
      return monthIndex === 11 && (currentMonth === 0 || currentMonth === 1);
    }
    
    return false;
  };

  // Función para obtener el color de la celda según el rendimiento
  const getCellColor = (value, record) => {
    if (!value || value === 0) return 'transparent';
    
    const numValue = parseFloat(value);
    const { min, sat, exc } = record;

    if (numValue >= exc) return '#f6ffed'; // Verde claro para excelente
    if (numValue >= sat) return '#e6f7ff'; // Azul claro para satisfactorio
    if (numValue >= min) return '#fff7e6'; // Amarillo claro para mínimo
    return '#fff1f0'; // Rojo claro para below mínimo
  };

  // Función para obtener el indicador de rendimiento
  const getPerformanceTag = (value, record) => {
    if (!value || value === 0) return null;
    
    const numValue = parseFloat(value);
    const { min, sat, exc } = record;

    if (numValue >= exc) return <Tag color="success">Exc</Tag>;
    if (numValue >= sat) return <Tag color="processing">Sat</Tag>;
    if (numValue >= min) return <Tag color="warning">Min</Tag>;
    return <Tag color="error">Bajo</Tag>;
  };

  const columns = [
    {
      title: "Indicador",
      dataIndex: "indicador",
      key: "indicador",
      fixed: "left",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      ...getColumnSearchProps("indicador"),
      render: (text, record) => (
        <Tooltip title={[4, 20, 19].includes(user.puesto) ? `${text} | Min: ${record.min} | Sat: ${record.sat} | Exc: ${record.exc}` : text}>
          <span style={{ fontWeight: '500' }}>{text}</span>
        </Tooltip>
      ),
    },
    ...([4, 20, 19].includes(user.puesto)
      ? [
          {
            title: <Tooltip title="Valor del indicador">Valor</Tooltip>,
            dataIndex: "valor",
            key: "valor",
            fixed: "left",
            width: 90,
            sorter: (a, b) => a.valor - b.valor,
            render: (text) => (
              <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '13px' }}>
                {text}%
              </Tag>
            ),
          },
        ]
      : []),
    ...([4, 20, 19].includes(user.puesto)
      ? [
          {
            title: <Tooltip title="Mínimo aceptable">Min</Tooltip>,
            dataIndex: "min",
            key: "min",
            width: 80,
            fixed: "left",
            render: (text) => <span style={{ color: '#faad14' }}>{text}%</span>,
          },
          {
            title: <Tooltip title="Satisfactorio">Sat</Tooltip>,
            dataIndex: "sat",
            key: "sat",
            width: 80,
            fixed: "left",
            render: (text) => <span style={{ color: '#1890ff' }}>{text}%</span>,
          },
          {
            title: <Tooltip title="Excelente">Exc</Tooltip>,
            dataIndex: "exc",
            key: "exc",
            width: 80,
            fixed: "left",
            render: (text) => <span style={{ color: '#52c41a' }}>{text}%</span>,
          },
        ]
      : []),
    ...([4, 20, 19].includes(user.puesto)
      ? [
          {
            title: "Puesto",
            dataIndex: "puesto",
            key: "puesto",
            width: 150,
            ellipsis: {
              showTitle: false,
            },
            render: (text, record) => (
              <Tooltip title={record.puesto ? record.puesto.nombre : "N/A"}>
                <Tag color="geekblue" style={{ fontSize: '12px' }}>
                  {record.puesto ? record.puesto.nombre : "N/A"}
                </Tag>
              </Tooltip>
            ),
            ...getColumnSearchProps("puesto"),
          },
        ]
      : []),
    ...monthNames.map((month, i) => ({
      title: month,
      dataIndex: `mes${i + 1}`,
      key: `mes${i + 1}`,
      width: 120,
      sorter: (a, b) => a[`mes${i + 1}`] - b[`mes${i + 1}`],
      editable: isMonthEditable(i),
      render: (text, record) =>
        isEditing(record, `mes${i + 1}`) ? (
          <EditableCell
            editing={isEditing(record, `mes${i + 1}`)}
            dataIndex={`mes${i + 1}`}
            title={month}
            inputType="number"
            record={record}
            onSave={save}
            onCancel={cancel}
          />
        ) : (
          <div
            className="editable-cell-value-wrap"
            style={{
              paddingRight: 24,
              padding: '8px',
              backgroundColor: getCellColor(text, record),
              borderRadius: '4px',
              cursor: isMonthEditable(i) ? 'pointer' : 'default',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: '32px',
            }}
            onClick={() => isMonthEditable(i) && edit(record, `mes${i + 1}`)}
          >
            <span style={{ fontWeight: text && text !== 0 ? '500' : 'normal' }}>
              {text || '-'}
            </span>
            {isMonthEditable(i) && (
              <Tooltip title="Clic para editar">
                <EditOutlined 
                  style={{ 
                    fontSize: '12px', 
                    color: '#1890ff',
                    opacity: 0.6
                  }} 
                />
              </Tooltip>
            )}
          </div>
        ),
    })),
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record, col.dataIndex),
        onSave: save,
        onCancel: cancel,
      }),
    };
  });

  // Filtrar los datos visibles según el puesto del usuario y búsqueda global
  const filteredData = indicadores
    .filter((indicador) => {
      return [2, 4, 20, 19].includes(user.puesto) || indicador.puestoId === user.puesto;
    })
    .map((indicador) => {
      const resultados = Array.from({ length: 12 }, (_, i) => ({
        [`mes${i + 1}`]: 0,
      })).reduce((acc, curr) => ({ ...acc, ...curr }), {});

      // Los resultados ya vienen filtrados por año desde el backend
      if (indicador.resultados && Array.isArray(indicador.resultados)) {
        indicador.resultados.forEach((resultado) => {
          resultados[`mes${resultado.mes}`] = resultado.resultado;
        });
      }

      return {
        key: indicador.id,
        indicador: indicador.indicador,
        valor: indicador.valor,
        min: indicador.min,
        sat: indicador.sat,
        exc: indicador.exc,
        puesto: indicador.puesto ? indicador.puesto.nombre : "N/A",
        ...resultados,
      };
    })
    .filter((item) => {
      if (!globalSearch) return true;
      const searchLower = globalSearch.toLowerCase();
      
      // Buscar en el indicador
      if (item.indicador.toLowerCase().includes(searchLower)) return true;
      
      // Buscar en el puesto (solo para administradores)
      if ([4, 20, 19].includes(user.puesto) && item.puesto.toLowerCase().includes(searchLower)) return true;
      
      // Buscar en los valores de los meses
      for (let i = 1; i <= 12; i++) {
        if (item[`mes${i}`] && item[`mes${i}`].toString().includes(searchLower)) return true;
      }
      
      return false;
    });

  return (
    <div style={{ height: "100vh", padding: "20px", backgroundColor: '#f0f2f5', overflow: 'auto' }}>
      <Spin
        size="large"
        spinning={loading}
        tip="Cargando..."
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        <div>
          <Row justify="space-between" align="middle" className="header-optima" style={{ marginBottom: '20px' }}>
            <Col>
              <div className="titulo-header" style={{ fontSize: '24px', fontWeight: 'bold' }}>
                Evaluación de Desempeño
              </div>
            </Col>
            {[4, 20, 19].includes(user.puesto) && (
              <Col>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setIsIndicadoresModalVisible(true)}
                  >
                    Administrar Indicadores
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setIsPuestosModalVisible(true)}
                  >
                    Administrar Puestos
                  </Button>
                </Space>
              </Col>
            )}
          </Row>

          {/* Card informativo persistente */}
          <Collapse 
            defaultActiveKey={['1']}
            style={{ marginBottom: '20px', borderLeft: '4px solid #1890ff' }}
          >
            <Collapse.Panel 
              header={
                <Space>
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  <strong>Información de Registro</strong>
                </Space>
              }
              key="1"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Space align="start" style={{ width: '100%' }}>
                    <CalendarOutlined style={{ fontSize: '16px', color: '#1890ff', marginTop: '2px' }} />
                    <div>
                      <strong>Meses editables:</strong>{' '}
                      <span style={{ color: '#595959' }}>
                        {(() => {
                          const editableMonths = [];
                          if (currentMonth >= 2 && selectedYear === currentYear) editableMonths.push(monthNames[currentMonth - 2]);
                          if (currentMonth >= 1 && selectedYear === currentYear) editableMonths.push(monthNames[currentMonth - 1]);
                          if (selectedYear === currentYear) editableMonths.push(monthNames[currentMonth]);
                          if (selectedYear === currentYear - 1 && (currentMonth === 0 || currentMonth === 1)) editableMonths.push('Diciembre');
                          
                          return editableMonths.length > 2 
                            ? `${editableMonths.slice(0, -1).join(', ')} y ${editableMonths[editableMonths.length - 1]}`
                            : editableMonths.join(' y ');
                        })()}
                      </span>
                    </div>
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <Space align="start" style={{ width: '100%' }}>
                    <ClockCircleOutlined style={{ fontSize: '16px', color: '#faad14', marginTop: '2px' }} />
                    <div>
                      <strong>Fecha límite:</strong>{' '}
                      <span style={{ color: '#595959' }}>
                        {new Date(currentYear, currentMonth + 1, 0).toLocaleDateString('es-ES', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                  </Space>
                </Col>
                <Col span={24}>
                  <Space align="start" style={{ width: '100%' }}>
                    <BulbOutlined style={{ fontSize: '16px', color: '#52c41a', marginTop: '2px' }} />
                    <div style={{ color: '#666', fontSize: '13px' }}>
                      <strong>Cómo usar:</strong> Haga clic en cualquier celda editable, ingrese el valor y presione el botón de confirmación para guardar.
                    </div>
                  </Space>
                </Col>
                {[4, 20, 19].includes(user.puesto) && (
                  <Col span={24}>
                    <div style={{ 
                      borderTop: '1px solid #e8e8e8', 
                      paddingTop: '12px', 
                      marginTop: '4px' 
                    }}>
                      <Space align="start" style={{ width: '100%' }}>
                        <BarChartOutlined style={{ fontSize: '16px', color: '#1890ff', marginTop: '2px' }} />
                        <div style={{ width: '100%' }}>
                          <strong style={{ marginRight: '12px' }}>Leyenda de rendimiento:</strong>
                          <Space size={8} wrap>
                            <Tag color="success" style={{ margin: 0, fontSize: '12px' }}>Excelente</Tag>
                            <Tag color="processing" style={{ margin: 0, fontSize: '12px' }}>Satisfactorio</Tag>
                            <Tag color="warning" style={{ margin: 0, fontSize: '12px' }}>Mínimo</Tag>
                            <Tag color="error" style={{ margin: 0, fontSize: '12px' }}>Bajo</Tag>
                          </Space>
                        </div>
                      </Space>
                    </div>
                  </Col>
                )}
              </Row>
            </Collapse.Panel>
          </Collapse>

          <div className="content" style={{ maxWidth: '100%', overflow: 'hidden' }}>
            <Card 
              title={
                <Space style={{ fontSize: '18px', fontWeight: 'bold' }}>
                  <BarChartOutlined />
                  <span>Métricas {selectedYear}</span>
                </Space>
              }
              bordered={false}
              style={{ width: '100%' }}
              extra={
                <Space size="middle" wrap>
                  <Select
                    value={selectedYear}
                    onChange={(value) => {
                      setSelectedYear(value);
                      setGlobalSearch('');
                    }}
                    style={{ width: 120 }}
                    placeholder="Año"
                    options={[
                      { value: currentYear - 1, label: `${currentYear - 1}` },
                      { value: currentYear, label: `${currentYear}` },
                    ]}
                  />
                  <Input.Search
                    placeholder="Buscar en la tabla..."
                    allowClear
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    onSearch={(value) => setGlobalSearch(value)}
                    style={{ width: 300 }}
                    enterButton
                  />
                </Space>
              }
            >
              <Form form={form} component={false}>
                <Table
                  components={{
                    body: {
                      cell: EditableCell,
                    },
                  }}
                  bordered
                  dataSource={filteredData}
                  columns={mergedColumns}
                  rowClassName="editable-row"
                  rowKey="key"
                  scroll={{ x: 1500, y: 'calc(100vh - 480px)' }}
                  size="middle"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} indicadores`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                  }}
                />
              </Form>
            </Card>
          </div>

          <ModalIndicadores
            visible={isIndicadoresModalVisible}
            onClose={() => setIsIndicadoresModalVisible(false)}
          />
          <ModalPuestos
            visible={isPuestosModalVisible}
            onClose={() => setIsPuestosModalVisible(false)}
          />
        </div>
      </Spin>
      {error && (
        <Alert
          message="Error al cargar los datos"
          description={error.message}
          type="error"
          showIcon
          style={{ marginTop: '20px' }}
        />
      )}
    </div>
  );
}

export default EvaluacionDesempeno;